import { useReactFlow } from '@xyflow/react'
import equal from 'fast-deep-equal'
import { useCallback, useEffect, useMemo, useState } from 'react'
import * as Y from 'yjs'
import { useUser } from '~/context/UserContext'
import { stringToColor } from '~/utils/stringToColor'

// 10 seconds of idle time before a cursor is removed from the map.
const MAX_IDLE_TIME = 10000

// The cursor state for a single client.
export interface Cursor {
  id: string
  color: string
  x: number
  y: number
  timestamp: number
  userName?: string
  userEmail?: string
}

/// Syncs the cursor state across all clients.
export function initializeCursorStateSynced(ydoc: Y.Doc) {
  const cursorsMap = ydoc.getMap<Cursor>('cursors')
  const cursorId = ydoc.clientID.toString()
  const cursorColor = stringToColor(cursorId)

  return function useCursorStateSynced() {
    const [cursors, setCursors] = useState<Cursor[]>([])
    const { screenToFlowPosition } = useReactFlow()

    const user = useUser()

    // Flush any cursors that have gone stale.
    const flush = useCallback(() => {
      const now = Date.now()

      for (const [id, cursor] of cursorsMap) {
        if (now - cursor.timestamp > MAX_IDLE_TIME) {
          cursorsMap.delete(id)
        }
      }
    }, [])

    const onMouseMove = useCallback(
      (event: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
        const position = screenToFlowPosition({
          x: event.clientX,
          y: event.clientY,
        })

        cursorsMap.set(cursorId, {
          id: cursorId,
          color: cursorColor,
          x: position.x,
          y: position.y,
          timestamp: Date.now(),
          userName: user.name,
          userEmail: user.email,
        })
      },
      [screenToFlowPosition, user.name, user.email]
    )

    useEffect(() => {
      const timer = window.setInterval(flush, MAX_IDLE_TIME)
      const observer = () => {
        setCursors([...cursorsMap.values()])
      }

      flush()
      setCursors([...cursorsMap.values()])
      cursorsMap.observe(observer)

      return () => {
        cursorsMap.unobserve(observer)
        window.clearInterval(timer)
      }
    }, [flush])

    const cursorsWithoutSelf = useMemo(
      () => cursors.filter(({ id }) => id !== cursorId),
      [cursors]
    )

    return [cursorsWithoutSelf, onMouseMove] as const
  }
}
